/** @flow */

import React, { useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import { Button, View, Text } from "@components";
import classNames from "classnames";
import { toDarkerColor } from "@utils/color";

import "./style.scss";

export type ChartType =
  | "bar"
  | "line"
  | "scatter"
  | "bubble"
  | "pie"
  | "doughnut"
  | "polarArea"
  | "radar";

export type ChartConfig = $Exact<{
  type: ChartType,
  data: Object,
  title: string,
  options?: Object,
  className?: string,
}>;

const canvasBackgroundColorPlugin = {
  id: "canvasBackgroundColor",
  beforeDraw: (chart, args, options) => {
    const { ctx } = chart;
    ctx.save();
    ctx.globalCompositeOperation = "destination-over";
    ctx.fillStyle = options.color || "#0f0";
    ctx.fillRect(0, 0, chart.width, chart.height);
    ctx.restore();
  },
};

// How much darker the percentage text is than the legend box behind it.
const defaultTextDarkeningPercent = 55;

function formatPercentage(value, total) {
  if (value === 0 && total === 0) return "0%";

  if (!total) {
    return null;
  }
  const percentage = (Number(value) / total) * 100;

  if (!isFinite(percentage) || percentage < 0) {
    return null;
  }

  return percentage < 1 ? "<1%" : `${Math.round(percentage)}%`;
}

const legendPercentagePlugin = {
  id: "legendPercentage",
  afterDatasetsDraw: chart => {
    const type = chart.config && chart.config.type;
    if (type !== "pie" && type !== "doughnut") {
      return;
    }

    const legend = chart.legend;
    if (!legend || legend.options.display === false || !legend.legendHitBoxes) {
      return;
    }

    const dataset = chart.data.datasets && chart.data.datasets[0];
    if (!dataset || !Array.isArray(dataset.data)) {
      return;
    }

    const total = dataset.data.reduce(
      (sum, value, index) => (chart.getDataVisibility(index) ? sum + Number(value) : sum),
      0,
    );
    const { ctx } = chart;
    const labelOptions = legend.options.labels || {};
    const boxWidth = labelOptions.boxWidth || 40;
    const font = labelOptions.font || {};
    const boxHeight = labelOptions.boxHeight || font.size || 12;
    const fontFamily = font.family || "Helvetica, Arial, sans-serif";

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    legend.legendItems.forEach((item, index) => {
      const hitBox = legend.legendHitBoxes[index];
      if (item.hidden || !hitBox) {
        return;
      }

      const text = formatPercentage(dataset.data[index], total);
      if (!text) {
        return;
      }

      let fontSize = Math.min(11, boxHeight - 1);
      ctx.font = `bold ${fontSize}px ${fontFamily}`;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (ctx.measureText(text).width <= boxWidth - 4) {
          break;
        }
        fontSize -= 1;
        ctx.font = `bold ${fontSize}px ${fontFamily}`;
      }

      ctx.fillStyle = toDarkerColor(item.fillStyle, defaultTextDarkeningPercent);
      ctx.fillText(text, hitBox.left + boxWidth / 2, hitBox.top + boxHeight / 2);
    });

    ctx.restore();
  },
};

export const defaultOptions = {
  backgroundColor: [
    "rgb(255, 221, 180)",
    "rgb(178, 247, 202)",
    "rgb(201, 232, 255)",
    "rgb(240, 206, 255)",
    "rgb(255, 205, 205)",
    "rgb(142, 222, 214)",
  ],
  borderColor: [
    "rgba(255, 221, 180,0.9)",
    "rgba(178, 247, 202,0.9)",
    "rgba(201, 232, 255,0.9)",
    "rgba(240, 206, 255,0.9)",
    "rgba(255, 205, 205,0.9)",
    "rgba(142, 222, 214,0.9)",
  ],
  responsive: true,
  maintainAspectRatio: false,
  animation: {
    duration: 500,
    easing: "easeOutQuart",
  },
  plugins: {
    legend: {
      position: "top",
      labels: {
        boxWidth: 40,
        boxHeight: 14,
        padding: 12,
      },
    },
    tooltip: {
      enabled: true,
    },
    colors: {
      enabled: true,
    },
    canvasBackgroundColor: {
      color: "#fff",
    },
  },
};

const DEFAULT_HEIGHT = 200;

// handle long labels by splitting in multiple lines
const maxCharsPerLine = 14;

function wrapLabel(label: string, maxChars: number = maxCharsPerLine): string | Array<string> {
  if (!label || label.length <= maxChars) {
    return label;
  }

  const lines = [];
  let currentLine = "";

  label.split(/\s+/).forEach(word => {
    if (!currentLine) {
      currentLine = word;
    } else if (`${currentLine} ${word}`.length <= maxChars) {
      currentLine = `${currentLine} ${word}`;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
}

const legendMaxCharsFallback = 18;
const legendCharWidthRatio = 0.55;

function computeLegendMaxChars(chart): number {
  const legend = chart.legend;
  const labelOptions = (legend && legend.options && legend.options.labels) || {};
  const fontSize = (labelOptions.font && labelOptions.font.size) || 12;
  const boxWidth = labelOptions.boxWidth || 40;
  const padding = labelOptions.padding || 10;

  if (!chart.width) {
    return legendMaxCharsFallback;
  }
  const available = chart.width * 0.4 - boxWidth - padding - fontSize / 2;
  const maxChars = Math.floor(available / (fontSize * legendCharWidthRatio));

  if (!isFinite(maxChars) || maxChars <= 0) {
    return legendMaxCharsFallback;
  }
  return Math.max(maxChars, 10);
}

// Chart.js cuts off long legend labels instead of wrapping them. Its legend
// renderer supports an array of lines as `item.text` (renderText + the column
// fit measure multi-line items correctly), so we run the default generator for
// the chart type and wrap each label.
function wrapArcLegendLabels(chart): Array<Object> {
  const type = chart.config && chart.config.type;
  const typeOverrides = Chart.overrides[type] || {};
  const typeLegendLabels =
    (typeOverrides.plugins &&
      typeOverrides.plugins.legend &&
      typeOverrides.plugins.legend.labels) ||
    {};
  const generateLabels =
    typeLegendLabels.generateLabels || Chart.defaults.plugins.legend.labels.generateLabels;
  const maxChars = computeLegendMaxChars(chart);

  return generateLabels(chart).map(item => ({
    ...item,
    text: typeof item.text === "string" ? wrapLabel(item.text, maxChars) : item.text,
  }));
}

const linePalette = defaultOptions.borderColor;

export default function ChartJS(props: ChartConfig) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const chartConfigKeyRef = useRef(null);

  const { height, isStacked, wrapXAxisLabels, labelsOverBars, ...restOptions } =
    props.options || {};

  const canvasBackgroundColorPlugin = {
    id: "canvasBackgroundColor",
    beforeDraw: (chart, args, options) => {
      const { ctx } = chart;
      ctx.save();
      ctx.globalCompositeOperation = "destination-over";
      ctx.fillStyle = options.color || "#0f0";
      ctx.fillRect(0, 0, chart.width, chart.height);
      ctx.restore();
    },
  };

  function isChartDataEmpty(chartData: any): boolean {
    if (!chartData || !Array.isArray(chartData.datasets) || chartData.datasets.length === 0) {
      return true;
    }
    return chartData.datasets.every(dataset => {
      if (!dataset || !Array.isArray(dataset.data) || dataset.data.length === 0) {
        return true;
      }
      return dataset.data.every(
        value => value == null || (typeof value === "number" && isNaN(value)),
      );
    });
  }

  // Arc charts (pie/doughnut/polarArea) draw nothing when all values sum to 0,
  // so a zero-sum dataset is effectively "no data".
  // Disabling that function for now since 0 values are actual data
  // Suggesing to consult the BE team for real No data response
  // https://git.alltimetech.co.uk/projects/FRONT/repos/tanager-app/pull-requests/4274/overview?commentId=15019
  function isArcDataAllZero(chartData: any): boolean {
    return false;
    // if (!chartData || !Array.isArray(chartData.datasets)) {
    //   return true;
    // }
    // return chartData.datasets.every(
    //   dataset =>
    //     !dataset || !Array.isArray(dataset.data) || dataset.data.every(value => !Number(value)),
    // );
  }
  //  "No data" is not "text instead of the grid" because that way messes up with
  // the layout and some animation (like from having data to not having data)
  const noDataMessagePlugin = {
    id: "noDataMessage",
    afterDraw: (chart, args, options) => {
      const type = chart.config && chart.config.type;
      const isArc = type === "pie" || type === "doughnut" || type === "polarArea";
      if (!isChartDataEmpty(chart.data) && !(isArc && isArcDataAllZero(chart.data))) {
        return;
      }
      const { ctx, chartArea } = chart;
      const area =
        chartArea && chartArea.width
          ? chartArea
          : { left: 0, top: 0, right: chart.width, bottom: chart.height };
      const centerX = (area.left + area.right) / 2;
      const centerY = (area.top + area.bottom) / 2;

      ctx.save();
      ctx.font = options.font || "16px sans-serif";
      ctx.fillStyle = options.color || "#999";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(options.text || "No data", centerX, centerY);
      ctx.restore();
    },
  };

  // labelsOverBars: instead of squishing all the labels in the bottom of the chart
  // we can draw each category label vertically over its bar rotated so the text reads
  // bottom-to-top, and anchored just above the x-axis
  const barLabelsOverBarsPlugin = {
    id: "barLabelsOverBars",
    afterDatasetsDraw: (chart, args, options) => {
      if (!options || !options.enabled) {
        return;
      }
      const meta = chart.getDatasetMeta(0);
      if (!meta || !Array.isArray(meta.data)) {
        return;
      }
      const { ctx, chartArea } = chart;
      const labels = chart.data.labels || [];
      const padding = typeof options.padding === "number" ? options.padding : 6;

      ctx.save();
      ctx.font = options.font || "12px sans-serif";
      ctx.fillStyle = options.color || "#555";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";

      meta.data.forEach((bar, index) => {
        const label = labels[index];
        if (label == null || label === "") {
          return;
        }
        ctx.save();
        ctx.translate(bar.x, chartArea.bottom - padding);
        ctx.rotate(-Math.PI / 2); // -90°: so the text go from bottom to top
        ctx.fillText(String(label), 0, 0);
        ctx.restore();
      });

      ctx.restore();
    },
  };

  // x-axis labels stay horizontal
  // long ones we split on more lines.
  const wrappedXAxisTicks = {
    maxRotation: 0,
    minRotation: 0,
    autoSkip: false,
    callback(value: any): string | Array<string> {
      return wrapLabel(this.getLabelForValue(value));
    },
  };

  function applyStackedDatasetStyles(chartData: any): any {
    if (!chartData || !Array.isArray(chartData.datasets)) {
      return chartData;
    }

    const palette = defaultOptions.backgroundColor;

    return {
      ...chartData,
      datasets: chartData.datasets.map((dataset, index) => ({
        backgroundColor: palette[index % palette.length],
        borderColor: "#fff",
        borderWidth: 1,
        ...dataset,
      })),
    };
  }

  function applyLineDatasetStyles(chartData: any): any {
    if (!chartData || !Array.isArray(chartData.datasets)) {
      return chartData;
    }

    return {
      ...chartData,
      datasets: chartData.datasets.map((dataset, index) => ({
        borderColor: linePalette[index % linePalette.length],
        backgroundColor: linePalette[index % linePalette.length],
        borderWidth: 2,
        pointRadius: 2,
        fill: false,
        tension: 0,
        ...dataset,
      })),
    };
  }

  function applyArcDatasetStyles(chartData: any): any {
    if (!chartData || !Array.isArray(chartData.datasets)) {
      return chartData;
    }

    const palette = defaultOptions.backgroundColor;

    return {
      ...chartData,
      datasets: chartData.datasets.map(dataset => ({
        backgroundColor: Array.isArray(dataset.data)
          ? dataset.data.map((value, index) => palette[index % palette.length])
          : palette,
        borderColor: "#fff",
        borderWidth: 1,
        ...dataset,
      })),
    };
  }

  function getChartConfigKey(data: Object | Array<Array<any>>, options: Object): string {
    try {
      return JSON.stringify({ data, options });
    } catch (e) {
      return "";
    }
  }

  const isArcType = props.type === "pie" || props.type === "doughnut" || props.type === "polarArea";

  const rawData = props.data;
  const hasMultipleDatasets =
    rawData && Array.isArray(rawData.datasets) && rawData.datasets.length > 1;
  let data = rawData;
  if (isArcType) {
    data = applyArcDatasetStyles(rawData);
  } else if (props.type === "line") {
    data = applyLineDatasetStyles(rawData);
  } else if (isStacked || hasMultipleDatasets) {
    data = applyStackedDatasetStyles(rawData);
  }

  const derivedScales =
    isStacked || wrapXAxisLabels || labelsOverBars
      ? (() => {
          const scales = restOptions.scales || {};
          const xTicks = {
            ...(wrapXAxisLabels ? wrappedXAxisTicks : {}),
            // labels are over the bars, so no need from axis ones
            ...(labelsOverBars ? { display: false } : {}),
            ...((scales.x || {}).ticks || {}),
          };
          const hasXTickOverrides = wrapXAxisLabels || labelsOverBars;
          return {
            scales: {
              ...scales,
              x: {
                ...(isStacked ? { stacked: true } : {}),
                ...(scales.x || {}),
                ...(hasXTickOverrides ? { ticks: xTicks } : {}),
              },
              y: { ...(isStacked ? { stacked: true } : {}), ...(scales.y || {}) },
            },
          };
        })()
      : {};
  const datasetCount = data && Array.isArray(data.datasets) ? data.datasets.length : undefined;
  const callerPlugins: Object = (restOptions: Object).plugins || {};
  const callerLegend: Object = callerPlugins.legend || {};
  // A zero-sum arc chart draws no arcs, so the legend would float next to
  // nothing - hide it and let only the "No data" message show.
  // INFO: isArcDataAllZero is disabled (check the message or the conversation https://git.alltimetech.co.uk/projects/FRONT/repos/tanager-app/pull-requests/4274/overview?commentId=15012)
  const arcHasNoData = isArcType && (isChartDataEmpty(data) || isArcDataAllZero(data));
  const autoHideLegend =
    !isArcType &&
    datasetCount !== undefined &&
    datasetCount <= 1 &&
    callerLegend.display === undefined;

  const options = {
    ...defaultOptions,
    ...restOptions,
    ...derivedScales,
    plugins: {
      ...defaultOptions.plugins,
      ...callerPlugins,
      legend: {
        ...defaultOptions.plugins.legend,
        // arc charts have one row per slice - on top they take too much place,
        // so we move the legend on the right side.
        ...(isArcType ? { position: "right" } : {}),
        ...callerLegend,
        // long legend labels get cut off on narrow containers, so for arc
        // charts we wrap them on several lines (a caller generateLabels wins).
        ...(isArcType
          ? {
              labels: {
                ...defaultOptions.plugins.legend.labels,
                generateLabels: wrapArcLegendLabels,
                ...(callerLegend.labels || {}),
              },
            }
          : {}),
        ...(autoHideLegend || arcHasNoData ? { display: false } : {}),
      },
      barLabelsOverBars: {
        enabled: !!labelsOverBars,
        ...(callerPlugins.barLabelsOverBars || {}),
      },
    },
  };

  // Chart.js don't care about `height` in options - for fixed height we must
  // turn off the aspect ratio and set the height on the wrapper.
  // Without height on the wrapper the canvas grow forever, so we put a default one.
  options.maintainAspectRatio = false;
  const resolvedHeight = height || DEFAULT_HEIGHT;
  const wrapperHeight = typeof resolvedHeight === "number" ? `${resolvedHeight}px` : resolvedHeight;

  const chartConfigKey = getChartConfigKey(data, options);

  useEffect(() => {
    if (!canvasRef.current) {
      return undefined;
    }

    chartConfigKeyRef.current = chartConfigKey;
    chartRef.current = new Chart(canvasRef.current, {
      type: props.type,
      data,
      options,
      plugins: [
        canvasBackgroundColorPlugin,
        barLabelsOverBarsPlugin,
        noDataMessagePlugin,
        legendPercentagePlugin,
      ],
    });

    return () => {
      chartRef.current && chartRef.current.destroy();
      chartRef.current = null;
    };
  }, [props.type]);

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }

    if (chartConfigKeyRef.current === chartConfigKey) {
      return;
    }

    chartConfigKeyRef.current = chartConfigKey;
    chartRef.current.data = data;
    chartRef.current.options = options;
    chartRef.current.update();
  }, [chartConfigKey, data, options]);

  function exportChart() {
    if (!chartRef.current) {
      return;
    }
    const link = document.createElement("a");
    link.href = chartRef.current.toBase64Image();
    link.download = `${props?.title || "Chart"}.png`;
    link.click();
  }
  return (
    <View className={classNames("chart-wrapper", props.className)} gap="4">
      <View className="header" direction="row" justifyContent="space-between">
        <Text type="h6" text={props.title} />
        <span className="material-icons export-btn d-print-none" onClick={exportChart}>
          download
        </span>
      </View>
      <View height={wrapperHeight || undefined} className="chart-js">
        <canvas ref={canvasRef} />
      </View>
    </View>
  );
}
